import { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from '@heroui/react';

import { useApi } from '../lib/api-context';
import type { FileTab } from '../hooks/useSessions';
import { MarkdownRenderer } from './MarkdownRenderer';

interface FileViewerPaneProps {
  tab: FileTab;
  isActive: boolean;
}

interface ViewerState {
  loading: boolean;
  error: string | null;
  text: string;
  mediaType: string;
}

const MD_MEDIA_TYPES = new Set(['text/markdown']);

/**
 * Renders one file tab. Markdown files pipe through `<MarkdownRenderer>`;
 * other text files render in a monospace `<pre>` for fidelity. Binary
 * files don't reach this pane (the explorer disables click on them).
 */
export function FileViewerPane({ tab }: FileViewerPaneProps) {
  const api = useApi();
  const [state, setState] = useState<ViewerState>({
    loading: true,
    error: null,
    text: '',
    mediaType: '',
  });

  const fetchFile = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await api.readFileText(tab.scope, tab.path, {
        sessionId: tab.sessionId,
      });
      setState({
        loading: false,
        error: null,
        text: result.text,
        mediaType: result.mediaType,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, [api, tab.scope, tab.path, tab.sessionId]);

  useEffect(() => {
    void fetchFile();
  }, [fetchFile]);

  const isMarkdown = inferIsMarkdown(state.mediaType, tab.path);
  const breadcrumbs =
    tab.scope === 'persistent'
      ? `/workspace/${tab.path}`
      : `/workspace/sessions/${tab.sessionId ?? '?'}/${tab.path}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b border-white/8 px-4 py-2 text-xs">
        <span className="opacity-60" translate="no" title={breadcrumbs}>
          {breadcrumbs}
        </span>
        <span className="ms-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onPress={() => void fetchFile()}
            isDisabled={state.loading}
          >
            Refresh
          </Button>
        </span>
      </header>
      <div className="scroll-pane min-h-0 flex-1 overflow-y-auto px-4 py-6">
        {state.loading && state.text === '' && (
          <div className="flex items-center gap-2 text-sm opacity-70">
            <Spinner size="sm" /> Loading…
          </div>
        )}
        {state.error && (
          <div className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            <p className="font-medium">Could not load file</p>
            <p className="mt-1 text-xs opacity-90">{state.error}</p>
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              onPress={() => void fetchFile()}
            >
              Retry
            </Button>
          </div>
        )}
        {!state.loading && !state.error && (
          <div className="mx-auto max-w-3xl">
            {isMarkdown ? (
              <MarkdownRenderer source={state.text} />
            ) : (
              <pre className="scroll-pane max-w-full overflow-x-auto rounded-md bg-black/30 px-3 py-2 text-xs leading-relaxed">
                {state.text}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function inferIsMarkdown(mediaType: string, path: string): boolean {
  if (MD_MEDIA_TYPES.has(mediaType)) return true;
  return /\.(md|markdown)$/i.test(path);
}
